import { useState } from 'react'
import { Link } from './nav'
import ProjectSecuritySetup from './ProjectSecuritySetup'
import { AlertTriangle, Search, ShieldCheck } from 'lucide-react'
import { useAdminMode } from '../hooks/useAdminMode'
import { pausedStatus, pauseTooltip, projectPause } from '../lib/projectPause'
import BugTable from './bug-management/BugTable'
import StatusBadge from './StatusBadge'
import type { Project } from '../data/projectTypes'
import { useUpdateProjectMutation, useGetProjectMembersQuery, useGetSecurityOwnershipQuery, useGetProjectSecurityResultsQuery } from '../services/projectDataApi'
import { BugCount, ProbeGroups, TargetCounts } from './SecurityProbeGroups'
import SecurityHistory from './SecurityHistory'
import useIsMobile from '../hooks/useIsMobile'
import './SecurityTab.css'

// The width at which an API row stops being a set of columns and stacks instead. Kept in step with the
// matching @media block in SecurityTab.css, which does the stacking.
const STACKED_ROWS = '(max-width: 700px)'

// The one origin every probed endpoint shares, or null if they don't share one (or a URL won't parse,
// in which case showing the whole thing is the safe answer).
function singleOrigin(targets: { url: string }[]): string | null {
  const origins = new Set<string>()
  for (const { url } of targets) {
    try {
      origins.add(new URL(url).origin)
    } catch {
      return null
    }
  }
  return origins.size === 1 ? [...origins][0] ?? null : null
}

export default function SecurityTab({ projectId, project }: { projectId: string; project: Project | null }) {
  const members = useGetProjectMembersQuery(projectId)
  const role = members.currentData?.role
  const canEdit = role === 'owner' || role === 'admin' || role === 'member'
  const exempt = project?.security_ownership_exempt === true
  const enabled = project?.enabled_polish_passes.includes('security') && project.security_probing_enabled
  const ownership = useGetSecurityOwnershipQuery({ projectId, targetUrl: project?.target_url ?? '', nonce: project?.security_nonce ?? '' }, {
    skip: !canEdit || exempt || !project?.security_nonce,
    pollingInterval: 15000, skipPollingIfUnfocused: true,
  })
  if (!project) return <div className="security-tab__state" role="status">Loading security…</div>
  if (enabled && (members.isLoading || (canEdit && !exempt && ownership.isLoading))) {
    return <div className="security-tab__state" role="status">Loading security…</div>
  }
  const verified = exempt || ownership.currentData?.latest?.verified === true
  return <div className="security-tab" data-testid="security-tab">
    <Link to={`/projects/${projectId}/overview`}>← Overview</Link>
    {!enabled || (canEdit && !verified) ? <>
      <ProjectSecuritySetup project={project} canEdit={canEdit} verified={verified} />
      {ownership.isError && <p role="alert">Could not check ownership. <button onClick={() => void ownership.refetch()}>Retry</button></p>}
    </> : <SecurityResults projectId={projectId} project={project} />}
    {canEdit && <SecurityTestingControl project={project} />}
  </div>
}

function SecurityResults({ projectId, project }: { projectId: string; project: Project | null }) {
  const showDetails = useAdminMode()
  const stacked = useIsMobile(STACKED_ROWS)
  const query = useGetProjectSecurityResultsQuery({ projectId, includeRequestDetails: showDetails }, { pollingInterval: 60_000, skipPollingIfUnfocused: true })
  if (query.isLoading) return <div className="security-tab__state" role="status">Loading security results…</div>
  if (query.isError) return <div className="security-tab__state security-tab__state--error" role="alert">Security results could not be loaded.</div>

  const scans = query.data?.scans ?? []
  const bugs = query.data?.bugs ?? []
  const latest = scans[0]
  const pause = projectPause(project)
  const scanTime = latest && (latest.completed_at ?? latest.started_at ?? latest.created_at)
  const generalRequests = latest?.general_groups.reduce((count, group) => count + group.request_count, 0) ?? 0
  const generalBugs = [...new Set(latest?.general_groups.flatMap(group => group.bug_ids) ?? [])]
  const scanBugs = [...new Set([...(latest?.targets.flatMap(target => target.bug_ids) ?? []), ...generalBugs])]
  // Where every probed endpoint is on the same host — the usual case, since these are the project's own
  // APIs — the host is repeated noise on a phone, where it costs a whole line of a two-line row. Drop it
  // from the rows and state it once above them. Left in place when a scan spans hosts, where it is the
  // difference between two endpoints.
  const sharedOrigin = stacked ? singleOrigin(latest?.targets ?? []) : null
  return (
    <div className="security-tab" data-testid="security-tab">
      <header className="security-tab__header">
        <div>
          <h2 className="project-tab-title">Security scan results</h2>
          {scanTime && <p className="project-tab-subtitle">{latest?.completed_at ? 'Last updated' : 'Scan started'} <time dateTime={scanTime}>{new Date(scanTime).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'long' })}</time></p>}
        </div>
        {latest && <span title={pauseTooltip(pause)}><StatusBadge status={pausedStatus(latest.status, pause)} /></span>}
      </header>

      {project && <div className="security-tab__project-status" role="status">
        <span title={pauseTooltip(pause)}><StatusBadge status={pause ? 'paused' : 'active'} /></span>
        <p><strong>{pause ? 'Project paused' : 'Project active'}</strong> — {pause
          ? 'Resume the project to continue security scanning and update these results.'
          : 'Results may update as further security scanning is performed.'}</p>
      </div>}

      <section className="security-tab__section">
        <h3 className="project-tab-section-title">Security bugs</h3>
        <BugTable bugs={bugs} projectId={projectId} loading={false} hasWebhook={!!project?.webhook_url} showKind={false} showEnvironment={false} showSecurityImpact />
      </section>

      {latest && latest.warnings.length > 0 && (
        <section className="security-tab__section security-tab__warnings" role="alert">
          <h3 className="project-tab-section-title"><AlertTriangle size={17} aria-hidden /> Warnings</h3>
          <ul>{latest.warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul>
        </section>
      )}

      {!latest ? (
        <section className="security-tab__empty"><ShieldCheck size={24} aria-hidden /><div><h3 className="project-tab-section-title">No security scan results yet</h3><p>Results will appear after a test run completes the Security pass.</p></div></section>
      ) : (
        <>
          <section className="security-tab__section">
            <h3 className="project-tab-section-title"><Search size={17} aria-hidden /> Pentest Scan <BugCount ids={scanBugs} /></h3>
            <p className="security-tab__section-description">{latest.requests_made} probe requests. Expand an API or General, then a testing category, to see the checks performed and associated findings.{sharedOrigin && <> All endpoints below are on <code>{sharedOrigin}</code>.</>}</p>
            {latest.requests_made > latest.request_details_available && <p className="security-tab__muted">Category information is available for {latest.request_details_available} of the {latest.requests_made} requests recorded in this scan.</p>}
            {/* Column headings over a list of disclosures rather than a real table: each row expands to
                its own probe detail, which a <table> can't hold. The heading row and the rows share one
                grid template (--target-cols), so the columns line up as if it were one. */}
            <div className="security-tab__target-table">
            {!stacked && (
              <div className="security-tab__target-head">
                <span>Method</span>
                <span>Endpoint</span>
                <span>Bugs</span>
                <span>Requests</span>
              </div>
            )}
            <div className="security-tab__targets">
              {latest.targets.map(target => (
                <details className="security-tab__target" key={`${target.method}-${target.url}`}>
                  <summary>
                    <code className="security-tab__method">{target.method}</code>
                    <code className="security-tab__endpoint" title={target.url}>{sharedOrigin ? target.url.slice(sharedOrigin.length) : target.url}</code>
                    <TargetCounts ids={target.bug_ids} requests={target.request_count} inline={stacked} />
                  </summary>
                  <div className="security-tab__target-detail">
                    <p>{target.source === 'conventional'
                      ? 'The probe runner constructed this endpoint from a common API path and the test account’s ID.'
                      : target.source === 'derived' ? 'The probe runner derived this object URL from an observed endpoint and the test account’s ID.'
                        : target.source === 'unrecorded' ? 'This endpoint was reached during probing. Its discovery source was not recorded.'
                          : 'This endpoint was observed in recorded traffic. The categories below show the security checks that ran against it.'}</p>
                    <p><strong>Inputs identified:</strong> {[...target.params, ...target.id_params].length ? [...new Set([...target.params, ...target.id_params])].join(', ') : 'None'}</p>
                    {target.probe_groups.length > 0 ? <ProbeGroups groups={target.probe_groups} projectId={projectId} showDetails={showDetails} bugs={bugs} hasWebhook={!!project?.webhook_url} /> : <p className="security-tab__muted">No vulnerability tests were run for this API.</p>}
                  </div>
                </details>
              ))}
              {latest.general_groups.length > 0 && <details className="security-tab__target security-tab__general">
                <summary><strong>General</strong><span /><TargetCounts ids={generalBugs} requests={generalRequests} inline={stacked} /></summary>
                <div className="security-tab__target-detail">
                  <p>Additional probes check authentication, object ownership, common API paths, and site-wide behavior.</p>
                  <ProbeGroups groups={latest.general_groups} projectId={projectId} showDetails={showDetails} bugs={bugs} hasWebhook={!!project?.webhook_url} />
                </div>
              </details>}
            </div>
            </div>
            {latest.targets.length === 0 && latest.general_groups.length === 0 && <p className="security-tab__muted">No APIs or probe requests were recorded in this scan.</p>}
          </section>
        </>
      )}

      <SecurityHistory projectId={projectId} items={query.data?.history ?? []} />

      {scans.length > 1 && (
        <section className="security-tab__section">
          <h3 className="project-tab-section-title">Previous scans</h3>
          <div className="security-tab__scan-table-wrap">
            <table className="security-tab__scan-table" data-testid="security-previous-scans">
              <thead>
                <tr>
                  <th>Scanned</th>
                  <th>Status</th>
                  <th>Bugs</th>
                  <th>APIs</th>
                  <th>Probe requests</th>
                </tr>
              </thead>
              <tbody>
                {scans.slice(1).map(scan => {
                  // Same timestamp precedence the tab header uses, so a scan is dated by when it
                  // finished wherever it is shown.
                  const at = scan.completed_at ?? scan.started_at ?? scan.created_at
                  return (
                    <tr key={scan.id}>
                      <td><time dateTime={at}>{new Date(at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}</time></td>
                      <td><StatusBadge status={scan.status} /></td>
                      <td>{scan.security_bug_count}</td>
                      <td>{scan.targets_identified}</td>
                      <td>{scan.requests_made}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  )
}

function SecurityTestingControl({ project }: { project: Project }) {
  const [update, updating] = useUpdateProjectMutation()
  const [error, setError] = useState('')
  // Selecting the pass opts in even while ownership verification still gates probing.
  const configured = project.enabled_polish_passes.includes('security')
  return <footer className="testing-settings">
    {!configured && <p role="status">Security testing is disabled.</p>}
    <button type="button" disabled={updating.isLoading} onClick={async () => {
      setError('')
      try {
        await update({ id: project.id, data: {
          enabled_polish_passes: configured ? project.enabled_polish_passes.filter(pass => pass !== 'security') : [...project.enabled_polish_passes, 'security'],
          security_probing_enabled: !configured,
        } }).unwrap()
      } catch { setError('Could not update security testing. Please try again.') }
    }}>{updating.isLoading ? 'Saving…' : configured ? 'Disable Security Testing' : 'Set up security testing'}</button>
    {error && <p role="alert">{error}</p>}
  </footer>
}
